"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DocumentRequestSchema = exports.RequestItemSchema = exports.RequestPartySchema = exports.DocumentPackSchema = exports.PACK_STATUSES = exports.PackItemSchema = exports.REQUEST_STATUSES = exports.REQUEST_ITEM_STATES = exports.DOCUMENT_CONTEXT_TYPES = void 0;
exports.deriveRequestStatus = deriveRequestStatus;
const zod_1 = require("zod");
const documents_1 = require("./documents");
const documentSigning_1 = require("./documentSigning");
/**
 * Document packs and document requests (spec 2026-08-26, §3).
 *
 * A PACK is a reusable checklist of documents a business process owes
 * ("Tenant Onboarding Pack" = MSA + Personal Guaranty + Privacy Policy).
 * A REQUEST is one live instance of that checklist, attached to one process
 * via a context — onboarding of tenant X, adding location Y, device order Z —
 * with the parties who must sign and the merge data accumulated so far.
 *
 * A request's AGREEMENT items become envelopes (frozen at the active published
 * version) the moment their merge data is complete; until then they sit in
 * `awaiting_data`, naming the missing fields. POLICY items are accepted, not
 * signed, so they never grow an envelope. The request's own status is DERIVED
 * from its items and never stored — `deriveRequestStatus` below is the one
 * place that rule lives.
 */
/**
 * WHY a request exists — the process that opened it, not the document's subject.
 *
 * The four counterparty processes were added 2026-08-31. They are what makes a
 * request list legible: without them a vendor agreement and an employment
 * agreement are both `manual` and indistinguishable in the Legal screens.
 *
 * APPENDED, never reordered. Widening this enum means an OLD reader rejects a
 * row carrying a new value, so shared-types and documents-ext-service (the only
 * service that reads these rows) must deploy together.
 */
exports.DOCUMENT_CONTEXT_TYPES = [
    'tenant_onboarding', 'location_add', 'device_order', 'manual',
    'vendor_agreement', 'employment', 'partner_agreement', 'software_agreement',
];
/**
 * `removed` is a STATE, not a delete: nothing in the documents system is ever
 * erased, and a removed item keeps its row, its reason and its audit trail.
 * `accepted` is the policy-side terminal; `executed` the agreement-side one.
 */
exports.REQUEST_ITEM_STATES = [
    'awaiting_data', 'ready', 'sent', 'partially_signed',
    'executed', 'accepted', 'declined', 'voided', 'expired', 'removed',
];
exports.REQUEST_STATUSES = ['draft', 'in_progress', 'blocked', 'complete'];
const hasText = (v) => typeof v === 'string' && v.trim().length > 0;
/**
 * One document a pack demands. Signer roles are which ROLES the eventual
 * envelope is addressed to; the actual people come from the request's parties
 * at instantiation time.
 */
exports.PackItemSchema = zod_1.z.object({
    documentId: zod_1.z.string().min(1),
    kind: zod_1.z.enum(documents_1.DOCUMENT_KINDS),
    required: zod_1.z.boolean(),
    signerRoles: zod_1.z.array(zod_1.z.enum(documentSigning_1.SIGNER_ROLES)),
}).refine((i) => (i.kind === 'policy' ? i.signerRoles.length === 0 : i.signerRoles.length > 0), {
    message: 'a policy is accepted, not signed (no signer roles); an agreement must name at '
        + 'least one signer role — an envelope addressed to nobody can never reach executed',
    path: ['signerRoles'],
});
exports.PACK_STATUSES = ['active', 'retired'];
/**
 * A pack stores document IDS, never version numbers. The version freezes per
 * request item at envelope creation — the active published version at that
 * instant — exactly as envelopes already freeze their template reference.
 */
exports.DocumentPackSchema = zod_1.z.object({
    packId: zod_1.z.string().min(1),
    name: zod_1.z.string().trim().min(1),
    description: zod_1.z.string().optional(),
    items: zod_1.z.array(exports.PackItemSchema),
    /** Retired packs stop being attachable; existing requests are untouched. */
    status: zod_1.z.enum(exports.PACK_STATUSES),
    createdAt: zod_1.z.string().min(1),
    updatedAt: zod_1.z.string().min(1),
    updatedBy: zod_1.z.string().min(1),
});
/** A person this request knows about, matched to pack signer roles by `role`. */
exports.RequestPartySchema = zod_1.z.object({
    role: zod_1.z.enum(documentSigning_1.SIGNER_ROLES),
    name: zod_1.z.string().trim().min(1),
    email: zod_1.z.string().email().optional(),
});
exports.RequestItemSchema = zod_1.z.object({
    requestId: zod_1.z.string().min(1),
    itemId: zod_1.z.string().min(1),
    documentId: zod_1.z.string().min(1),
    kind: zod_1.z.enum(documents_1.DOCUMENT_KINDS),
    required: zod_1.z.boolean(),
    state: zod_1.z.enum(exports.REQUEST_ITEM_STATES),
    /** Frozen when the envelope is created; absent while awaiting data. */
    templateVersionNo: zod_1.z.number().int().positive().optional(),
    envelopeId: zod_1.z.string().optional(),
    /** Names only, present while `awaiting_data` — the early warning, by field. */
    missingFields: zod_1.z.array(zod_1.z.string()).optional(),
    removedReason: zod_1.z.string().optional(),
    updatedAt: zod_1.z.string().optional(),
}).refine((i) => i.state !== 'removed' || hasText(i.removedReason), { message: 'removing an item must record why — removal is an audit event, never an erasure',
    path: ['removedReason'] });
exports.DocumentRequestSchema = zod_1.z.object({
    requestId: zod_1.z.string().min(1),
    tenantId: zod_1.z.string().min(1),
    context: zod_1.z.object({
        type: zod_1.z.enum(exports.DOCUMENT_CONTEXT_TYPES),
        refId: zod_1.z.string().min(1),
    }),
    packId: zod_1.z.string().optional(),
    /**
     * Who countersigns for us on every envelope this request creates. Absent
     * means the company profile's default signatory — the usual case, where the
     * CEO signs everything. A request a sales rep owns names theirs.
     */
    companySignatoryId: zod_1.z.string().optional(),
    parties: zod_1.z.array(exports.RequestPartySchema),
    /**
     * Accumulates via PATCH until complete; values are pre-stringified, matching
     * envelope merge data — a number here would put a coerced value in a contract.
     */
    mergeData: zod_1.z.record(zod_1.z.string()),
    createdAt: zod_1.z.string().min(1),
    createdBy: zod_1.z.string().min(1),
    updatedAt: zod_1.z.string().optional(),
});
/**
 * The request's status, DERIVED from its items on every read — never stored,
 * so it cannot drift (same rule as `activeVersion`/`isSuperseded`).
 *
 * Precedence: blocked > in_progress > complete-or-draft. A single dead or
 * data-starved REQUIRED item blocks the whole request no matter how much else
 * is moving — that item needs a human, and "in progress" would hide it.
 * Optional items can fail without blocking completion; removed items do not
 * count at all. An empty (or fully-removed) request is `draft`, never
 * `complete`: a checklist with nothing on it has not been satisfied, it has
 * not been written yet.
 */
function deriveRequestStatus(items) {
    const live = items.filter((i) => i.state !== 'removed');
    if (!live.length)
        return 'draft';
    const requiredItems = live.filter((i) => i.required);
    const blocked = requiredItems.some((i) => i.state === 'awaiting_data' || i.state === 'declined' || i.state === 'voided' || i.state === 'expired');
    if (blocked)
        return 'blocked';
    const moving = live.some((i) => i.state === 'sent' || i.state === 'partially_signed');
    if (moving)
        return 'in_progress';
    const complete = requiredItems.length > 0
        && requiredItems.every((i) => i.state === 'executed' || i.state === 'accepted');
    if (complete)
        return 'complete';
    return 'draft';
}
//# sourceMappingURL=documentRequests.js.map